import { PositionProps } from './CommonTypes'

/**************************
 * Community Shared Props *
 *************************/
export enum CommunityType {
  SCHOOL = 'school',
  LEA = 'lea',
}

/**************************
 * Community Carpool Props *
 **************************/
export enum DefaultCarpoolType {
  ARRIVAL = 'arrival',
  DISMISSAL = 'dismissal',
}
export const isDefaultCarpoolType = (type: string): type is DefaultCarpoolType => {
  return Object.values(DefaultCarpoolType).includes(type as DefaultCarpoolType)
}
export type CarpoolType = DefaultCarpoolType | string

// Represents possible carpool driver status types
export enum CarpoolDriverStatus {
  /** The driver has been designated. */
  DESIGNATED = 'designated',
  /** The driver is enroute. */
  EN_ROUTE = 'en_route',
  /** The driver has announced their arrival for pickup. */
  ARRIVED = 'arrived',
  /** The driver has picked up all students. */
  PICKED_UP = 'picked_up', // picked_up_{studentId} for per student pickup
  /** The driver has dropped off all students. */
  DROPPED_OFF = 'dropped_off', // dropped_off_{studentId} for per student dropoff
  /** The driver has finished all pickups and dropoffs for the carpool. */
  COMPLETED = 'completed',
}

/******************************************
 * Community Carpool Stats Shared Props *
 ******************************************/
export enum TimePeriod {
  // Default period
  AllTime = 'alltime',
  // Future periods
  Next365Days = 'next365days',
  Next90Days = 'next90days',
  Next30Days = 'next30days',
  Next7Days = 'next7days',
  // Current period
  Today = 'today',
  ToDate = 'todate',
  // Past periods
  Last7Days = 'last7days',
  Last30Days = 'last30days',
  Last90Days = 'last90days',
  Last365Days = 'last365days',
  // Calendar periods
  ThisMonth = 'thismonth',
  LastMonth = 'lastmonth',
  ThisYear = 'thisyear',
  LastYear = 'lastyear',
}
export type TimePeriodType = `${TimePeriod}`
export function isValidTimePeriod(value: string): value is TimePeriod {
  return Object.values(TimePeriod).includes(value as TimePeriod)
}
export interface CarpoolMetrics {
  userName: string
  drivesCount: number
  ridesCount: number
  byStudent: Record<string, { studentName: string; ridesCount: number }>
}
export interface CarpoolStatsProps {
  totalDrives: number
  totalRides: number
  byUser: Record<string, CarpoolMetrics>
}

/******************************************
 * Community Student Carpool Shared Props *
 ******************************************/
export enum VehicleProps {
  NONE = 0,
  HIRE_TRANSPORT_SERVICE = 1,
}
export type CustomTimeProps =
  | -1 // User has disabled carpool explicitly on this day
  | number // User has enabled carpool explicitly on this day
export type DefaultTimeProps =
  | -1 // Carpool disabled by default on all days
  | null // Carpool enabled by default at community default time on all days
  | number // Carpool enabled by default at user specified time on all days (unused)
export type DateTimeProps = { [date: string]: number }
export type DateDriverProps = { [date: string]: string }
export type DatePassengerProps = { [date: string]: string[] }
export type DateDriverStatusProps = { [date: string]: CarpoolDriverStatus }
export type DateCarpoolProps = { [date: string]: { time: number; driverId: string } }

/**
 * Community Input Props
 */
export interface UpdateCommunityStudentCarpoolRequestInputProps {
  carpoolType: CarpoolType
  carpoolRequests?: DateTimeProps
  defaultTime?: DefaultTimeProps
}

export interface UpdateCommunityStudentCarpoolDetailInputProps {
  carpoolType: CarpoolType
  assignCarpoolDrivers?: DateDriverProps
  removeCarpoolPassengers?: DatePassengerProps
  updateDriverStatus?: DateDriverStatusProps
}

export interface UpdateCommunityStudentCarpoolInputProps {
  carpoolType: CarpoolType
  carpools: DateCarpoolProps
}

/**
 * Community Carpool Input Props
 */
export interface CommunityCarpoolStatsQueryParams {
  poolType?: CarpoolType
  poolDriverId?: string
  poolStudentId?: string
  poolTimePeriods?: TimePeriodType[]
  poolStartDate?: string
  poolEndDate?: string
}
export interface CommunityCarpoolsQueryParams {
  poolType: CarpoolType
  poolDriverId?: string
  poolStartDate: string
  poolEndDate: string
}

/**
 * Community Student Carpool Input Props
 */
export interface CommunityStudentCarpoolsQueryParams {
  poolType: CarpoolType
  poolDriverId?: string
  poolDate: string
}

/**
 * Community Output Props
 */
export interface EnrolledCommunityOutputProps {
  communityId: string
  communityName: string
  country: string
  adminArea: string
  subAdminArea?: string
  locality: string
  subLocality?: string
  postalCode: string
}

/**
 * Community Carpool Output Props
 */
export type CommunityCarpoolRequestOutputProps = {
  // User Props
  userId: string
  userName: string
  userRelationship: string
  userPhotoLink: string
  // Student Props
  studentId: string
  scheduleGroupName: string
  studentName: string
  studentActivities: string[]
  // Carpool Type Dependent Props
  position: PositionProps
  vehicle?: VehicleProps
  seatsFilled?: number
  seatsAvailable?: number
  comments?: string
  defaultTime?: DefaultTimeProps
  customTime?: CustomTimeProps
}

export type CommunityCarpoolStatsOutputProps = Partial<Record<TimePeriodType | 'custom', CarpoolStatsProps>>

export type CommunityCarpoolOutputProps = {
  carpoolType: CarpoolType
  carpoolScheduledAt: string
  carpoolDriverId: string
  carpoolDriverStatus: CarpoolDriverStatus
}

/**
 * Community Student Carpool Output Props
 */
export type CommunityStudentCarpoolOutputProps = {
  carpoolType: CarpoolType
  carpoolScheduledAt: string
  carpoolDriverId: string
  carpoolDriverStatus: CarpoolDriverStatus
  carpoolStudentIds: string[]
}
